import { createAsyncThunk } from '@reduxjs/toolkit';
import { httpRequest } from '~/adapters/httpRequest';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import { normalize } from 'normalizr';
import { host } from '~/config/settings/worktango.settings';
import { LearningContent } from '~/interfaces/learning_content.interface';
import { GetTagsDocument, GetTagsQueryResult } from '~/gql/graphql';

const fetchLearningContents = createAsyncThunk<{ [id: number]: LearningContent }>(
  'learning-content/fetchLearningContent',
  async () => {
    const url = `${host}/api/learning-content/v1/learning-contents/`;
    const res = await httpRequest<any>(url);

    const data = res?.learningContents || [];
    const learningContents = data.map(transformers.learningContentFrom);
    const normalizedLearningContents = normalize(learningContents, [schemas.learningContent]);

    return normalizedLearningContents;
  },
);

const fetchLearningTags = createAsyncThunk('learning-content/fetchLearningTags', async (empty, { rejectWithValue }) => {
  const res = await graphqlRequest<GetTagsQueryResult>(GetTagsDocument);

  if (!res || res?.error) return rejectWithValue(res?.error);

  const learningTags = transformers.learningTagsFrom(res.data?.tags);
  const normalizedData = normalize(learningTags, [schemas.learningTag]);

  return normalizedData;
});

const changeContentEffectiveness = createAsyncThunk<
  { [id: number]: LearningContent },
  { id: number; effectiveness: number }
>('learning-content/changeContentEffectiveness', async ({ id, effectiveness }) => {
  const url = `${host}/api/learning-content/v1/learning-contents/${id}`;
  const res = await httpRequest<any>(url, {
    method: 'PUT',
    body: {
      learningContent: {
        effectiveness,
      },
    },
  });

  const learningContent = transformers.learningContentFrom(res?.learningContent);
  const normalizedLearningContent = normalize(learningContent, schemas.learningContent);

  return normalizedLearningContent;
});

const changeContentCategories = createAsyncThunk<
  { [id: number]: LearningContent },
  { id: number; categories: number[] }
>('learning-content/changeContentCategories', async ({ id, categories }) => {
  const url = `${host}/api/learning-content/v1/learning-contents/${id}`;
  const res = await httpRequest<any>(url, {
    method: 'PUT',
    body: {
      learningContent: {
        categories,
      },
    },
  });

  const learningContent = transformers.learningContentFrom(res?.learningContent);
  const normalizedLearningContent = normalize(learningContent, schemas.learningContent);

  return normalizedLearningContent;
});

const toggleContentStar = createAsyncThunk<{ [id: number]: LearningContent }, { id: number; isStarred?: boolean }>(
  'learning-content/toggleContentStar',
  async ({ id, isStarred = true }) => {
    const url = `${host}/api/learning-content/v1/learning-contents/${id}/toggle-star`;
    const res = await httpRequest<any>(url, {
      method: 'PUT',
      body: {
        isStarred,
      },
    });

    const learningContent = transformers.learningContentFrom(res?.learningContent);
    const normalizedLearningContent = normalize(learningContent, schemas.learningContent);

    return normalizedLearningContent;
  },
);

const markContentAsVisited = createAsyncThunk<{ [id: number]: LearningContent }, number>(
  'learning-content/markContentAsVisited',
  async (id) => {
    const url = `${host}/api/learning-content/v1/learning-contents/${id}/register-visit`;
    const res = await httpRequest<any>(url, { method: 'PUT' });

    const learningContent = transformers.learningContentFrom(res?.learningContent);
    const normalizedLearningContent = normalize(learningContent, schemas.learningContent);

    return normalizedLearningContent;
  },
);

const rateLearningContent = createAsyncThunk<{ [id: number]: LearningContent }, { id: number; rating: number }>(
  'learning-content/rateLearningContent',
  async ({ id, rating }) => {
    const url = `${host}/api/learning-content/v1/learning-contents/${id}/rate`;
    const res = await httpRequest<any>(url, {
      method: 'PUT',
      body: {
        rating,
      },
    });

    const learningContent = transformers.learningContentFrom(res?.learningContent);
    const normalizedLearningContent = normalize(learningContent, schemas.learningContent);

    return normalizedLearningContent;
  },
);

export const learningContentActions = {
  fetchLearningContents,
  fetchLearningTags,
  changeContentEffectiveness,
  changeContentCategories,
  toggleContentStar,
  markContentAsVisited,
  rateLearningContent,
};
